import React from 'react';
import { kebabCase } from './helpers';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Link } from 'react-router-dom';

import { getScoreForName } from 'components/Explorer/util/sentiment_helpers';
import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';

const linkToSentimentFilteredMentions = (params, sentimentName) => {
  const filters = { ...(params.filters || {}), 'sentiment[]': getScoreForName(sentimentName) };
  return QueryParams.locationFor(QueryParams.withFilters(params, filters), '/mentions');
};

const trackPercentageLinkClicked = (sentimentName) => () => {
  Analytics.trackEvent('Sentiment Summary: Sentiment Percentage link clicked', { sentimentValue: sentimentName });
};

const SummaryBar = ({ value: { sentimentName, percentage } }) => {
  if (percentage === 0) return <></>;
  const dataSentimentName = kebabCase(sentimentName);
  const percentageLabel = Math.ceil(percentage || 0);
  return (
    <div
      className={`bar ${dataSentimentName}`}
      style={{ width: `${percentage}%` }}
      data-tooltip-id={`${dataSentimentName}-bar`}
      data-tooltip-content={`${sentimentName} (${percentageLabel}% of mentions)`}
      data-testid={`sentiment-bar:${dataSentimentName}:${percentage}%`}
    >
      <ReactTooltip id={`${dataSentimentName}-bar`} effect="solid" />
    </div>
  );
};

const SummaryBarChart = ({
  summary: { strongNegative, negative, neutralNegative, neutral, neutralPositive, positive, strongPositive }
}) => (
  <div className="Sentiment-Summary-chart" aria-hidden>
    <SummaryBar value={strongNegative} />
    <SummaryBar value={negative} />
    <SummaryBar value={neutralNegative} />
    <SummaryBar value={neutral} />
    <SummaryBar value={neutralPositive} />
    <SummaryBar value={positive} />
    <SummaryBar value={strongPositive} />
  </div>
);

const SummaryLabel = ({ value: { sentimentName, percentage }, params }) => {
  const hasSentiment = percentage > 0;
  const percentageLabel = percentage || 0;

  const body = (
    <div data-testid={`sentiment-label:${kebabCase(sentimentName)}`}>
      <strong>{sentimentName}</strong>
      <p>{percentageLabel}% of mentions</p>
    </div>
  );

  return (
    <div className="sentiment-label">
      <div className={`swatch ${kebabCase(sentimentName)}`}></div>
      {hasSentiment ? (
        <Link
          data-testid={`sentiment-link:${kebabCase(sentimentName)}`}
          to={linkToSentimentFilteredMentions(params, sentimentName)}
          onClick={trackPercentageLinkClicked(sentimentName)}
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </div>
  );
};

const SummaryLabels = ({ summary, params }) => {
  const { strongNegative, negative, neutralNegative, neutral, neutralPositive, positive, strongPositive } = summary;
  return (
    <ul className="Sentiment-Summary-labels">
      <li>
        <SummaryLabel value={strongNegative} params={params} />
      </li>
      <li>
        <SummaryLabel value={negative} params={params} />
      </li>
      <li>
        <SummaryLabel value={neutralNegative} params={params} />
      </li>
      <li>
        <SummaryLabel value={neutral} params={params} />
      </li>
      <li>
        <SummaryLabel value={neutralPositive} params={params} />
      </li>
      <li>
        <SummaryLabel value={positive} params={params} />
      </li>
      <li>
        <SummaryLabel value={strongPositive} params={params} />
      </li>
    </ul>
  );
};

const SummaryBarContent = ({ sentimentSummary }) => {
  const params = useQueryParams();

  return (
    <>
      <SummaryBarChart summary={sentimentSummary} />
      <SummaryLabels summary={sentimentSummary} params={params} />
    </>
  );
};

export default SummaryBarContent;
